import { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSession } from "@/context/Session";
import { presetFor } from "@/lib/presets";
import { UploadCloud, X, HardDrive, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { KindIcon } from "@/components/Badges";
import { AudioRecorder } from "@/components/AudioRecorder";
import { t } from "@/i18n";

const kindOf = (f) =>
  f.type.startsWith("image/") ? "photo" : f.type.startsWith("video/") ? "video" : f.type.startsWith("audio/") ? "audio" : "document";

function Dropzone({ onFiles }) {
  const ref = useRef();
  const [over, setOver] = useState(false);
  return (
    <div
      onClick={() => ref.current.click()}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onFiles([...e.dataTransfer.files]); }}
      className={`rounded-xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${over ? "border-[var(--accent-primary)] bg-[var(--accent-subtle)]" : "border-[var(--border-color)] hover:border-[var(--border-active)]"}`}
      data-testid="capture-dropzone"
    >
      <UploadCloud className="mx-auto text-[var(--accent-primary)]" size={30} />
      <p className="font-semibold mt-3">{t("Drop video, audio, photos or documents")}</p>
      <p className="text-sm faint mt-1">MP4 / MOV / MP3 / M4A / JPG / PNG / PDF / TXT · {t("up to 5 files, 100 MB each")}</p>
      <input ref={ref} type="file" multiple hidden accept="video/*,audio/*,image/*,.pdf,.txt,.md,.csv" onChange={(e) => { onFiles([...e.target.files]); e.target.value = ""; }} data-testid="capture-file-input" />
    </div>
  );
}

export default function Capture() {
  const api = useApi();
  const nav = useNavigate();
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState("");
  const [drive, setDrive] = useState("");
  const [params] = useSearchParams();
  const { me } = useSession();
  const [title, setTitle] = useState(params.get("title") || "");
  const [audience, setAudience] = useState(params.get("audience") || "auto");
  const [busy, setBusy] = useState(false);
  const total = files.length + (note.trim() ? 1 : 0) + drive.split("\n").filter((l) => l.trim()).length;

  const submit = async () => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("note", note);
    fd.append("drive_urls", drive);
    fd.append("title_hint", title);
    fd.append("audience", audience);
    setBusy(true);
    try {
      const { data } = await api.post("/captures", fd);
      toast.success(t("Capture received — extracting and drafting your SOP"));
      nav(`/sops/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="page grid lg:grid-cols-12 gap-s">
      <div className="lg:col-span-8 stack">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight rise">{t("Capture")}</h1>
        <div className="card stack-sm rise d1">
          <label className="mono-label">{t("Who can see this")}</label>
          <select className="field" value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="capture-audience-select">
            <option value="auto">{t("Let AI suggest")}</option><option value="team">{t("Team")}</option>
            <option value="customer,team">{t("Customer + Team")}</option><option value="customer">{t("Customer only")}</option><option value="cashier">{t("Cashier only")}</option>
            {me.role === "admin" && <option value="private">{t("Private (owner only)")}</option>}
          </select>
          <label className="mono-label">{t("Procedure name (optional)")}</label>
          <input className="field" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t(presetFor(me.org?.preset).captureTitlePh)} data-testid="capture-title-input" />
        </div>
        <div className="card stack-sm rise d2">
          <div className="flex items-center justify-between"><span className="mono-label">{t("Media & files")}</span><AudioRecorder onRecorded={(f) => setFiles((p) => [...p, f])} /></div>
          <Dropzone onFiles={(fs) => setFiles((p) => [...p, ...fs])} />
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-3 text-sm py-2 border-b border-[var(--border-color)]" data-testid={`capture-file-item-${i}`}>
              <KindIcon kind={kindOf(f)} /><span className="flex-1 truncate">{f.name}</span>
              <span className="faint text-xs">{(f.size / 1048576).toFixed(1)} MB</span>
              <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="faint hover:text-[var(--status-error)]" data-testid={`capture-file-remove-${i}`}><X size={16} /></button>
            </div>
          ))}
        </div>
        <div className="card stack-sm rise d3">
          <label className="mono-label">{t("Typed notes")}</label>
          <textarea className="field min-h-[140px]" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t(presetFor(me.org?.preset).captureNotePh)} data-testid="capture-note-textarea" />
        </div>
        <div className="card stack-sm rise d4">
          <label className="mono-label flex items-center gap-2"><HardDrive size={13} /> {t("Google Drive links (one per line)")}</label>
          <textarea className="field min-h-[80px] font-mono text-sm" value={drive} onChange={(e) => setDrive(e.target.value)} placeholder="https://docs.google.com/document/d/…" data-testid="capture-drive-textarea" />
          <p className="text-xs faint">{t("Share as “Anyone with the link”.")}</p>
        </div>
      </div>
      <aside className="lg:col-span-4">
        <div className="card stack-sm lg:sticky lg:top-24 rise d2">
          <p className="mono-label">{t("This capture")}</p>
          <p className="text-4xl font-extrabold" data-testid="capture-source-count">{total}</p>
          <p className="text-sm muted">{t(total === 1 ? "source" : "sources")}</p>
          <button className="btn btn-primary w-full" disabled={!total || busy} onClick={submit} data-testid="capture-submit-button">
            <Sparkles size={16} /> {t(busy ? "Uploading…" : "Extract & draft SOP")}
          </button>
        </div>
      </aside>
    </div>
  );
}
